import { useState } from 'react';
import {
  useCaseManagers, useCaseTeam, useAssignCaseManager, useGrantCoverage,
  useExtendCoverage, useEndCoverage, caseManagerLabel, type CaseTeamMember,
} from '../api/assignments.ts';
import { ApiError } from '../api/client.ts';
import { formatDate } from '../dates.ts';

const field =
  'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 ' +
  'text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';

const DEPARTMENT = 'Social Work';

/**
 * Who holds this case, and who has been lent it.
 *
 * Open by default, unlike the court order panel: a case manager arriving at a
 * client they do not recognise asks whose it is before anything else, and a
 * covering manager needs to see how many days they have left without hunting
 * for it.
 *
 * The two buttons are shown only when the server said `may_assign`. A case
 * manager sees the same panel with no buttons — not a disabled button they can
 * press for a 403, and not a hidden truth: they can see the cover is in place
 * and who granted it, they just cannot change it.
 */
export function CaseTeamPanel({ clientId }: { clientId: string }) {
  const team = useCaseTeam(clientId);
  const [form, setForm] = useState<'assign' | 'cover' | null>(null);
  // Said after a reassignment: the open tasks that followed the case to the new case manager (118).
  const [moved, setMoved] = useState<string | null>(null);

  if (team.isLoading) {
    return <Shell><p className="text-neutral-600">Loading the case team…</p></Shell>;
  }
  if (!team.data) {
    return <Shell><p className="text-neutral-500">The case team could not be loaded.</p></Shell>;
  }

  const live = team.data.team.filter((m) => m.is_live);
  const primary = live.find((m) => m.assignment_kind === 'Primary') ?? null;
  const covering = live.filter((m) => m.assignment_kind === 'Covering');
  const past = team.data.team.filter((m) => !m.is_live);

  return (
    <Shell>
      {primary ? (
        <Row
          name={primary.staff_name}
          detail={`case manager since ${formatDate(primary.assigned_on)}`}
        />
      ) : (
        <p className="text-neutral-700">
          <span className="font-semibold text-amber-800">No case manager.</span>{' '}
          Visits recorded for this client count, but nobody is carrying it.
        </p>
      )}

      {covering.map((c) => (
        <Coverage
          key={c.assignment_id}
          cover={c}
          clientId={clientId}
          mayAssign={team.data.may_assign}
        />
      ))}

      {/* A case that has ended takes no new assignments — said, not just
          hidden. A missing button reads as a permission problem, and the
          Director who holds every permission there is would have nothing to go
          on. Who held the case still shows: that is what matters once it is
          over. */}
      {team.data.case_ended && (
        <p className="mt-3 text-sm text-neutral-600">
          This client is recorded as {team.data.case_ended.toLowerCase()}. The case is
          closed to new assignments.
        </p>
      )}

      {team.data.may_assign && form === null && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" onClick={() => setForm('assign')} className={secondary}>
            {primary ? 'Reassign' : 'Assign a case manager'}
          </button>
          <button type="button" onClick={() => setForm('cover')} className={secondary}>
            Grant temporary coverage
          </button>
        </div>
      )}

      {moved && form === null && (
        <p role="status" className="mt-3 rounded-lg border-l-8 border-note-edge bg-note-bg px-3 py-2 text-sm font-semibold text-note-fg">
          {moved}
        </p>
      )}

      {form === 'assign' && (
        <AssignForm
          clientId={clientId}
          current={primary}
          onDone={(said) => { setMoved(said ?? null); setForm(null); }}
        />
      )}
      {form === 'cover' && (
        <CoverForm
          clientId={clientId}
          current={primary}
          covering={covering}
          onDone={() => setForm(null)}
        />
      )}

      {past.length > 0 && <Past entries={past} />}
    </Shell>
  );
}

const secondary =
  'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold ' +
  'text-neutral-800 hover:bg-neutral-50 active:bg-neutral-100';

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <section className="panel">
      <h2 className="panel-bar">
        Case team
      </h2>
      <div className="px-4 pt-1 pb-4">{children}</div>
    </section>
  );
}

function Row({ name, detail }: { name: string; detail: string }) {
  return (
    <p className="text-neutral-900">
      <span className="font-semibold">{name}</span>
      <span className="text-neutral-600"> — {detail}</span>
    </p>
  );
}

/**
 * A live cover, with what is left of it.
 *
 * The days are counted by the server from the agency's own today, never from
 * the tablet's clock — a tablet an hour behind midnight must not tell somebody
 * they have a day they do not.
 */
function Coverage({
  cover, clientId, mayAssign,
}: { cover: CaseTeamMember; clientId: string; mayAssign: boolean }) {
  const extend = useExtendCoverage(clientId);
  const end = useEndCoverage(clientId);
  const busy = extend.isPending || end.isPending;
  const left = cover.days_left ?? 0;

  return (
    <div className="mt-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2">
      <p className="text-neutral-900">
        <span className="font-semibold">{cover.staff_name}</span>
        <span className="text-neutral-700">
          {' '}covering until {formatDate(cover.covers_until!)} —{' '}
          {left === 0 ? 'last day' : left === 1 ? '1 day left' : `${left} days left`}
        </span>
      </p>
      {cover.granted_reason && (
        <p className="mt-0.5 text-sm text-neutral-600">
          {cover.granted_reason}
          {cover.granted_by ? ` · granted by ${cover.granted_by}` : ''}
        </p>
      )}
      {mayAssign && (
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => extend.mutate({ assignment_id: cover.assignment_id, days: 7 })}
            className={secondary}
          >
            Extend a week
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => end.mutate({ assignment_id: cover.assignment_id })}
            className={secondary}
          >
            End now
          </button>
        </div>
      )}
      <Failure error={extend.error ?? end.error} />
    </div>
  );
}

function AssignForm({
  clientId, current, onDone,
}: { clientId: string; current: CaseTeamMember | null; onDone: (said?: string) => void }) {
  const managers = useCaseManagers(DEPARTMENT, true, clientId);
  const assign = useAssignCaseManager(clientId);
  const [staffId, setStaffId] = useState('');

  return (
    <form
      className="mt-3 border-t border-neutral-200 pt-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!staffId) return;
        const to = managers.data?.case_managers.find((m) => m.id === staffId)?.full_name ?? 'the new case manager';
        assign.mutate({ staff_user_id: staffId, department: DEPARTMENT }, {
          onSuccess: (r) => {
            const n = r.tasks_moved ?? 0;
            onDone(n === 0 ? undefined : `${n === 1 ? 'One open task' : `${n} open tasks`} on this client moved to ${to}.`);
          },
        });
      }}
    >
      <label className={label} htmlFor="assign-to">
        {current ? 'Reassign this client to' : 'Assign this client to'}
      </label>
      <select
        id="assign-to"
        className={field}
        value={staffId}
        onChange={(e) => setStaffId(e.target.value)}
      >
        <option value="">Choose a case manager…</option>
        {(managers.data?.case_managers ?? [])
          .filter((m) => m.id !== current?.staff_user_id)
          .map((m) => (
            <option key={m.id} value={m.id}>
              {caseManagerLabel(m)}
            </option>
          ))}
      </select>
      {current && (
        <p className="mt-2 text-sm text-neutral-600">
          {current.staff_name}’s assignment ends today. It stays on the record. Their open tasks on this client move
          to the new case manager.
        </p>
      )}
      <Failure error={assign.error} />
      <Actions busy={assign.isPending} disabled={!staffId} verb="Assign" onCancel={onDone} />
    </form>
  );
}

function CoverForm({
  clientId, current, covering, onDone,
}: {
  clientId: string;
  current: CaseTeamMember | null;
  covering: CaseTeamMember[];
  onDone: () => void;
}) {
  const managers = useCaseManagers(DEPARTMENT, true, clientId);
  const grant = useGrantCoverage(clientId);
  const [staffId, setStaffId] = useState('');
  const [days, setDays] = useState('14');
  const [reason, setReason] = useState('');
  const taken = new Set(covering.map((c) => c.staff_user_id));

  return (
    <form
      className="mt-3 border-t border-neutral-200 pt-3"
      onSubmit={(e) => {
        e.preventDefault();
        const n = Number(days);
        if (!staffId || !reason.trim() || !Number.isInteger(n) || n < 1) return;
        grant.mutate(
          { staff_user_id: staffId, department: DEPARTMENT, days: n, reason: reason.trim() },
          { onSuccess: onDone },
        );
      }}
    >
      <label className={label} htmlFor="cover-who">Give access to</label>
      <select
        id="cover-who"
        className={field}
        value={staffId}
        onChange={(e) => setStaffId(e.target.value)}
      >
        <option value="">Choose a case manager…</option>
        {(managers.data?.case_managers ?? [])
          .filter((m) => m.id !== current?.staff_user_id && !taken.has(m.id))
          .map((m) => (
            <option key={m.id} value={m.id}>
              {caseManagerLabel(m)}
            </option>
          ))}
      </select>

      <label className={`${label} mt-3`} htmlFor="cover-days">For how many days</label>
      <input
        id="cover-days"
        className={field}
        type="number"
        inputMode="numeric"
        min={1}
        max={180}
        value={days}
        onChange={(e) => setDays(e.target.value)}
      />

      <label className={`${label} mt-3`} htmlFor="cover-why">Why</label>
      <input
        id="cover-why"
        className={field}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Annual leave, medical leave, caseload cover…"
        maxLength={200}
      />

      <p className="mt-2 text-sm text-neutral-600">
        Access ends by itself on the last day. The client stays on
        {current ? ` ${current.staff_name}’s` : ' the primary'} caseload and does not
        count towards the covering manager’s.
      </p>
      <Failure error={grant.error} />
      <Actions
        busy={grant.isPending}
        disabled={!staffId || !reason.trim()}
        verb="Grant coverage"
        onCancel={onDone}
      />
    </form>
  );
}

function Actions({
  busy, disabled, verb, onCancel,
}: { busy: boolean; disabled: boolean; verb: string; onCancel: () => void }) {
  return (
    <div className="mt-3 flex gap-2">
      <button
        type="submit"
        disabled={busy || disabled}
        className="min-h-[48px] flex-1 rounded-lg bg-neutral-900 px-4 font-semibold text-white
                   disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed"
      >
        {busy ? 'Saving…' : verb}
      </button>
      <button type="button" onClick={onCancel} className={secondary}>Cancel</button>
    </div>
  );
}

function Failure({ error }: { error: Error | null }) {
  if (!error) return null;
  const api = error instanceof ApiError ? error : null;
  return (
    <p className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-900">
      {error.message}
      {api?.hint && <span className="block text-rose-700">{api.hint}</span>}
    </p>
  );
}

/** What the record keeps: every assignment that has ended, and when. */
function Past({ entries }: { entries: CaseTeamMember[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 border-t border-neutral-200 pt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-sm font-semibold text-neutral-600 hover:text-neutral-900"
      >
        {open ? 'Hide' : `Earlier (${entries.length})`}
      </button>
      {open && (
        <ul className="mt-2 space-y-1 text-sm text-neutral-600">
          {entries.map((e) => (
            <li key={e.assignment_id}>
              {e.staff_name} — {e.assignment_kind === 'Covering' ? 'covered' : 'held'}{' '}
              {formatDate(e.assigned_on)} to{' '}
              {formatDate(e.ended_on ?? e.covers_until ?? e.assigned_on)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
